// src/utils/eventHelper.js
// Used in EventDetailScreen.js and CreateRacingEventScreen.js
import { GAME_PROFILES } from '../constants/gameData';
import { PAYOUT_STRUCTURES } from '../constants/payoutStructures';
import { getFunctions, httpsCallable } from 'firebase/functions'; 

export const COLORS = {
  GOLD: '#FCD000',
  BG: '#0b0b0c',     // match Header background
  PANEL: '#111',
  STROKE: '#1a1a1a',
  TEXT: '#E7ECFF',
  MUTED: '#9AA0B4',
};

export const toCents = (n) => Math.max(0, Math.round(Number(n || 0)));
export const fmtUSD = (c) => {
  const dollars = Math.max(0, Number(c || 0)) / 100;
  return '$' + dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
export const ordinal = (n) => {
  const s = ['th','st','nd','rd'], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const toDateLike = (v) => {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v?.seconds != null) return new Date(v.seconds * 1000);
  const d = new Date(v);
  return Number.isFinite(d.getTime()) ? d : null;
};
export const fmtDate = (d) => {
  const dt = toDateLike(d);
  if (!dt) return '—';
  try {
    return dt.toLocaleString(undefined, { year:'numeric', month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });
  } catch { return dt.toISOString(); }
};

/** Expand stageBands → map of rank → cents (handles {from,to} or {minRank,maxRank}). */
export function expandBandsToMap(bands = []) {
  const out = {};
  (bands || []).forEach((b) => {
    const fromRaw = (b.from ?? b.minRank ?? 0);
    const toRaw = (b.to != null ? b.to : (b.maxRank != null ? b.maxRank : b.from));
    const from = Number(fromRaw ?? 0);
    const to = Number(toRaw ?? 0);
    const prize = Number(b.totalCents ?? b.total ?? 0);
    if (!Number.isFinite(from) || !Number.isFinite(to)) return;
    for (let r = from; r <= to; r++) out[r] = prize;
  });
  return out;
}
export const maxPaidRank = (...maps) =>
  Math.max(0, ...maps.map(m => Math.max(0, ...Object.keys(m).map(n => Number(n) || 0))));




// ✅ 1. ADD THIS FUNCTION (from your archive) FOR FIGHTING/BRACKET EVENTS
/** Convert stageBands into compact per-band rows (per-winner focus). */
/** Convert stageBands into compact per-band rows (per-winner focus). */
export function bandsToRows(stageBands = []) {
  return (stageBands || []).map((b) => {
    const fromRaw = (b.from ?? b.minRank ?? 1);
    const toRaw = (b.to != null ? b.to : (b.maxRank != null ? b.maxRank : b.from));
    const from = Number(fromRaw ?? 1);
    const to = Number(toRaw ?? from);
    const winners = Math.max(1, to - from + 1);
    // For brackets, the value is already per-winner, so we calculate total
    const perWinnerCents = Number(b.totalCents ?? b.total ?? 0);
    return {
      key: `${from}-${to}`, from, to, winners, perWinnerCents,
      label: from === to ? `${ordinal(from)} Place` : `${ordinal(from)}–${ordinal(to)}`,
    };
  });
}


// ✅ 2. ADD THIS NEW FUNCTION, BUILT SPECIFICALLY FOR ALL RACING EVENTS
/**
 * Generates payout rows for ALL racing event types.
 * It calculates prizes from the total prize pool and the percentages in the payout structure.
 */
export function generateRacingRows(bands = [], netPoolCents = 0, opts = {}) {
  const totalEntrants = Math.max(1, Number(opts.entrants || 0));
  const rows = [];
  if (!netPoolCents || !bands?.length) return rows;

  for (const band of bands) {
    const stageName = String(band?.stage || 'Finals');
    const stagePercent = Number(band?.percent || 0);
    const stageTotalCents = netPoolCents * (stagePercent / 100);

    // Parse "Heat of N" → N, otherwise assume a single race (Finals)
    let unitSize = null;
    const m = /Heat of\s*([0-9,]+)/i.exec(stageName);
    if (m) unitSize = parseInt(String(m[1]).replace(/,/g, ''), 10);
    const heatCount = unitSize ? Math.max(1, Math.ceil(totalEntrants / unitSize)) : 1;

    if (Array.isArray(band.subBands) && band.subBands.length) {
      // Complex (Finals and each Heat stage with tiers inside)
      for (const sb of band.subBands) {
        const [from, toRaw] = sb?.places || [1];
        const to = toRaw ?? from;
        const winnersPerHeat = Math.max(1, (to - from + 1));
        const tierPercent = Number(sb?.percentOfBand || 0);
        const subBandTotal = stageTotalCents * (tierPercent / 100);
        const perWinnerCents = subBandTotal / (winnersPerHeat * heatCount);

        if (perWinnerCents > 0) {
          rows.push({
            key: `${from}-${to}`,
            from, to,
            winners: winnersPerHeat,                   // per heat winners count
            perWinnerCents,                            // per driver, per heat
            label: sb?.tier || `${from}–${to}`,
            stage: stageName,
            stagePercent,
            tierPercent,
            unitSize,                                  // e.g., 64 for "Heat of 64"
            heatCount,
          });
        }
      }
    } else {
      // Simple “showdown” style (still respect heatCount==1 for Finals)
      const from = Number(band?.minRank ?? 1);
      const to = Number(band?.maxRank ?? from);
      const winnersPerHeat = Math.max(1, (to - from + 1));
      const perWinnerCents = stageTotalCents / (winnersPerHeat * heatCount);

      if (perWinnerCents > 0) {
        rows.push({
          key: `${from}-${to}`,
          from, to,
          winners: winnersPerHeat,
          perWinnerCents,
          label: band?.tier || stageName || (from === to ? `${from}` : `${from}–${to}`),
          stage: stageName,
          stagePercent,
          tierPercent: 100,
          unitSize,
          heatCount,
        });
      }
    }
  }

  // Sort by rank (helps align across full vs current)
  return rows.sort((a, b) => a.from - b.from);
}



/** Align two band row arrays (e.g., Full vs Current) by their [from–to] keys. */
export function alignBandRows(fullRows = [], currentRows = []) {
  const mapFull = Object.fromEntries((fullRows || []).map(r => [r.key, r]));
  const mapNow  = Object.fromEntries((currentRows || []).map(r => [r.key, r]));
  const keys = Array.from(new Set([...Object.keys(mapFull), ...Object.keys(mapNow)])).sort((a,b) => {
    const [af] = a.split('-').map(Number); const [bf] = b.split('-').map(Number);
    return af - bf;
  });
  return keys.map(k => ({ key: k, full: mapFull[k] || null, now: mapNow[k] || null }));
}

/** Resolve payout structure id (incl. racing families). */
const sizeLadder = [16384, 8192, 4096, 2048, 1024, 1000, 512, 256, 128, 96, 64, 48, 32, 24, 16, 8, 4];
const suffixFor = (n) => (n >= 1000 ? `${Math.round(n / 1000)}k` : String(n));

const findPsKey = (id) => {
  const needle = String(id || '').toLowerCase();
  return Object.keys(PAYOUT_STRUCTURES).find(k => k.toLowerCase() === needle) || null;
};

export function resolvePayoutStructureId(ev, isTournament) {
  if (ev?.payoutStructureId) return ev.payoutStructureId;

  const mode = String(ev?.mode || ev?.format || '').toLowerCase();
  const gp = GAME_PROFILES?.[ev?.game] || {};
  const gtype = (gp?.type || ev?.type || '').toLowerCase();

  // Racing
  if (mode === 'racing' || gtype === 'racing') {
    const abbrRaw = (gp?.abbr || ev?.abbr || ev?.game || '').toString();
    const abbr =
      /gt7/i.test(abbrRaw) ? 'gt7' :
      /fzh|forza/i.test(abbrRaw) ? 'fzh' :
      /mkd|mkw|mario/i.test(abbrRaw) ? 'mkd' :
      abbrRaw.toLowerCase();

    const heats = Number(ev?.heatsCount || 0);
    const grid  = Number(ev?.gridSize || 0);
    const total = Number(ev?.entriesExpected || (heats && grid ? heats * grid : 0));

    if (heats === 1) {
      const suf = abbr === 'gt7' ? 'top-8' : abbr === 'fzh' ? 'top-12' : 'top-6';
      // const id = `racing-${abbr}-${suf}`;
      // return PAYOUT_STRUCTURES[id] ? id : null;
      const maybe = findPsKey(`racing-${abbr}-${suf}`);
      return maybe || null;
    }
    const hit = sizeLadder.find(n => total >= n) || sizeLadder[sizeLadder.length - 1];
    // const id = `racing-${abbr}-${suffixFor(hit)}`;
    // if (PAYOUT_STRUCTURES[id]) return id;
    const id = `racing-${abbr}-${hit >= 1000 ? Math.round(hit/1000)+'k' : hit}`;
    const exact = findPsKey(id);
    if (exact) return exact;


    // const coarse = grid >= 24 ? `racing-${abbr}-32` : `racing-${abbr}-16`;
    // return PAYOUT_STRUCTURES[coarse] ? coarse : (PAYOUT_STRUCTURES[`racing-${abbr}-4`] ? `racing-${abbr}-4` : null);
    const coarse = findPsKey(grid >= 24 ? `racing-${abbr}-32` : `racing-${abbr}-16`);
    if (coarse) return coarse;
    const tiny = findPsKey(`racing-${abbr}-4`);
    return tiny || null;
  }

  // Non-racing
  if (mode === 'h2h') return 'h2h-winner-take-all';
  if (mode === 'wgn') {
    const t = Math.min(6, Math.max(3, Number(ev?.streakTarget || ev?.wgnTarget || 3)));
    return `wgn-${t}`;
  }
  if (mode === 'ladder' || mode === 'season-ladder') return 'season-ladder-100';

  // Brackets by size
  if (isTournament) {
    const n = Number(ev?.bracketSize || ev?.maxTeams || ev?.maxEntries || 0);
    if (n >= 16384) return 'bracket-16k-team';
    if (n >= 8192)  return 'bracket-8k-team';
    if (n >= 4096)  return 'bracket-4k-team';
    if (n >= 2048)  return 'bracket-2k-team';
    if (n >= 1024)  return 'bracket-1k-team';
    const table = [512,256,128,64,32,16,8,4];
    const hit = table.find(t => n >= t);
    return hit ? `bracket-${hit}-team` : null;
  }

  return null;
}

/** Entry helpers */
export const hasPaidEntry = (entry) => {
  if (!entry) return false;
  const truthyFlags =
    entry.paid === true ||
    entry.debitSucceeded === true ||
    entry.balanceHold === true;
  const amountSeen =
    Number(entry.paidCents || entry.amountPaidCents || entry.amount_captured || 0) > 0;
  const timestampSeen =
    !!(entry.paidAt || entry.capturedAt || entry.paymentCapturedAt);
  const s = String(
    entry.paymentStatus || entry.status || entry.chargeStatus || entry.piStatus || ''
  ).toLowerCase();
  const okStatus = ['paid','succeeded','success','complete','completed','captured','authorized'].includes(s);
  return truthyFlags || amountSeen || timestampSeen || okStatus;
};


/** Entry Helper for Team Captains */
export async function joinEventAsTeamWithDebit({ eventId, collection, teamId, rosterUids, idempotencyKey }) {
  const functions = getFunctions(undefined, 'us-central1');
  const call = httpsCallable(functions, 'joinEventAsTeamWithDebit');
  const idem = idempotencyKey || (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
  return await call({ eventId, collection, teamId, rosterUids, idempotencyKey: idem });
}

// Function to help with Showdon Payout calculation. 
// should calculate no matter how many should be in the grid
// should default to winner take all if payout is too small to be fair
export function computeRacingShowdownPayouts(opts) {
  const {
    netPoolCents,
    gridSize,
    entryFeeCents = 500,
    gameKey,
    alpha = 1.35,
    champBoostPct = 0.05,
    tailFloorPct = 0.60,
    roundTickCents = 25,
  } = opts || {};

  if (!Number.isFinite(netPoolCents) || netPoolCents <= 0) {
    return { amountsCents: [], paidSpots: 0, weights: [], floorsCents: [], roundingDelta: 0 };
  }

  // Infer paid spots (K) if not provided
  let paidSpots = Number(opts?.paidSpots || 0);
  if (!paidSpots && gameKey) {
    const key = String(gameKey).toLowerCase();
    if (key === 'gt7') paidSpots = 8;
    else if (key === 'fzh') paidSpots = 12;
    else if (key === 'mkw') paidSpots = 6;
  }
  if (!paidSpots) {
    // Default heuristic: pay half of grid, capped between 6 and 12
    paidSpots = Math.min(12, Math.max(6, Math.floor((gridSize || 8) / 2)));
  }
  paidSpots = Math.max(1, Math.min(paidSpots, gridSize || paidSpots));

  // --- 1) Base power-curve weights (1st gets biggest). weight_i = (K+1-i)^alpha
  const raw = [];
  for (let i = 1; i <= paidSpots; i++) raw.push(Math.pow((paidSpots + 1 - i), alpha));
  const rawSum = raw.reduce((a, b) => a + b, 0) || 1;
  const weights = raw.map(w => w / rawSum);

  // --- 2) Champion boost (extra fixed % of pool to 1st)
  const boostCents = Math.round(netPoolCents * Math.max(0, Math.min(champBoostPct, 0.25)));
  let poolForCurve = netPoolCents - boostCents;

  // --- 3) Floors (bubble protection). Last paid place gets at least X% of entry fee.
  let floorsCents = new Array(paidSpots).fill(0);
  const tailFloor = Math.round(Math.max(0, entryFeeCents * Math.max(0, Math.min(tailFloorPct, 1)))); // <-- fixed parens
  floorsCents[paidSpots - 1] = tailFloor;

  // If floors exceed pool (rare), scale them down
  const floorsTotal = floorsCents.reduce((a, b) => a + b, 0);
  let floorScale = 1;
  if (floorsTotal > netPoolCents) floorScale = netPoolCents / floorsTotal;
  if (floorScale !== 1) floorsCents = floorsCents.map(f => Math.floor(f * floorScale));

  const reservedForFloors = floorsCents.reduce((a, b) => a + b, 0);
  const poolForWeights = Math.max(0, poolForCurve - reservedForFloors);

  // --- 4) Allocate curve + boost + floors
  let provisional = weights.map(w => Math.floor(poolForWeights * w));
  // distribute rounding from weights to hit poolForWeights exactly
  let curveSum = provisional.reduce((a, b) => a + b, 0);
  let curveDelta = poolForWeights - curveSum; // remaining cents to allocate
  for (let j = 0; curveDelta > 0 && j < provisional.length; j++, curveDelta--) provisional[j]++;

  // add champion boost
  provisional[0] = (provisional[0] || 0) + boostCents;

  // add floors
  provisional = provisional.map((v, idx) => v + (floorsCents[idx] || 0));

  // --- 5) Friendly rounding to tick (e.g., $0.25)
  const tick = Math.max(1, Math.floor(roundTickCents));
  let rounded = provisional.map(v => Math.round(v / tick) * tick);

  // ensure monotone non-increasing after rounding
  for (let i = 1; i < rounded.length; i++) {
    if (rounded[i] > rounded[i - 1]) rounded[i] = rounded[i - 1];
  }

  // --- 6) Fix sum to match netPoolCents exactly, prefer tick-sized adjustments
  let sumRounded = rounded.reduce((a, b) => a + b, 0);
  let delta = netPoolCents - sumRounded; // positive => need to add

  if (delta !== 0) {
    const step = delta > 0 ? tick : -tick;
    let remaining = Math.abs(delta);

    // First, adjust by full ticks while preserving monotonicity
    while (remaining >= tick) {
      let movedThisPass = false;
      for (let i = 0; i < rounded.length && remaining >= tick; i++) {
        const prev = i === 0 ? Number.POSITIVE_INFINITY : rounded[i - 1];
        const next = i === rounded.length - 1 ? 0 : rounded[i + 1];
        const candidate = rounded[i] + step;
        const okUp   = (step > 0) && candidate <= prev;
        const okDown = (step < 0) && candidate >= next && candidate >= 0;
        if (okUp || okDown) {
          rounded[i] = candidate;
          remaining -= tick;
          movedThisPass = true;
        }
      }
      if (!movedThisPass) break; // avoid infinite loop
    }

    // If remainder isn't a multiple of tick, nudge 1st place by the leftover cents
    if (remaining > 0) {
      const leftover = delta > 0 ? remaining : -remaining;
      rounded[0] = Math.max(0, rounded[0] + leftover);
      remaining = 0;
    }

    // Re-enforce monotonicity after all adjustments
    for (let i = 1; i < rounded.length; i++) {
      if (rounded[i] > rounded[i - 1]) rounded[i] = rounded[i - 1];
    }

    sumRounded = rounded.reduce((a, b) => a + b, 0);
  }

  return {
    amountsCents: rounded,
    paidSpots,
    weights,
    floorsCents,
    roundingDelta: (sumRounded - netPoolCents),
  };
}

// --- Racing showdown helpers ---
export function racingShowdownPaidSpots(gameKey) {
  const k = String(gameKey || '').toLowerCase();
  if (k === 'gt7') return 8;
  if (k === 'fzh') return 12;
  return 6; // MKW default
}

/**
 * Decide WTA vs multi-pay automatically, then return amounts.
 * - If entrants < minEntrantsForMulti (defaults to game cutoff), return WTA.
 * - Also collapse to WTA if 1st place would be too small (minFirstPrizeX * entryFee).
 */
export function computeRacingShowdownPayoutsAuto({
  entrants,
  gridSize,
  netPoolCents,
  entryFeeCents = 500,
  gameKey,
  paidSpots,
  minEntrantsForMulti,      // optional override
  minFirstPrizeX = 4.0,     // “1st should be at least 4x entry”
  tailFloorPct = 0.60,
}) {
  const n = Math.max(0, Number(entrants || 0));
  if (n < 2 || !Number.isFinite(netPoolCents) || netPoolCents <= 0) {
    return { mode: 'none', amountsCents: [] };
  }

  const defaultK = paidSpots || racingShowdownPaidSpots(gameKey);
  const multiCut = Math.max(2, Number(minEntrantsForMulti || defaultK));

  // Too small => Winner Take All
  if (n < multiCut) {
    return { mode: 'wta', amountsCents: [Math.round(netPoolCents)] };
  }

  // Multi-pay: cap K by entrants & grid
  const K = Math.min(defaultK, n, gridSize || defaultK);
  const out = computeRacingShowdownPayouts({
    netPoolCents,
    gridSize,
    entryFeeCents,
    gameKey,
    paidSpots: K,
    tailFloorPct,
  });

  const first = out.amountsCents?.[0] || 0;
  if (first < entryFeeCents * minFirstPrizeX) {
    // If the top prize looks weak, keep it exciting with WTA
    return { mode: 'wta', amountsCents: [Math.round(netPoolCents)] };
  }

  return { mode: 'multi', amountsCents: out.amountsCents };
}

